import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";
import { usePageTitle } from "../hooks/usePageTitle";

function WinnerTag() {
  return (
    <span className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wide text-brand-700 bg-brand-50 rounded px-1.5 py-0.5 align-middle">
      Vencedor
    </span>
  );
}

/**
 * Stacks the deck name with its taxonomy (subarchetype + archetype pill).
 * Used for both player1 and player2 cells in the Partidas table so each
 * deck cell carries the same density of information the Resultados table
 * does, without forcing extra columns. The deck name itself is rendered as
 * a link to its decklist page when a snapshot exists (`deckHref`).
 */
function DeckCell({
  deckName,
  subarchetype,
  subarchetypeHref,
  archetype,
  deckHref,
}: {
  deckName: string | null;
  subarchetype: string | null;
  subarchetypeHref: string | null;
  archetype: string | null;
  deckHref: string | null;
}) {
  if (!deckName) return <span className="text-[#6b6356]">—</span>;
  const nameEl = deckHref ? (
    <Link
      to={deckHref}
      className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
    >
      {deckName}
    </Link>
  ) : (
    <span className="text-[#6b6356]">{deckName}</span>
  );
  return (
    <div className="flex flex-col gap-0.5">
      {nameEl}
      {(subarchetype || archetype) && (
        <span className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {subarchetype &&
            (subarchetypeHref ? (
              <Link
                to={subarchetypeHref}
                className="text-[#8a8170] hover:text-brand-700 hover:underline decoration-dotted underline-offset-2"
              >
                {subarchetype}
              </Link>
            ) : (
              <span className="text-[#8a8170]">{subarchetype}</span>
            ))}
          {archetype && (
            <span className="inline-block rounded-full bg-brand-50 text-brand-900 font-semibold px-1.5 py-0.5">
              {archetype}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

export function EventPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);

  const eventQuery = useQuery({
    queryKey: ["event", eventId],
    queryFn: () => api.event(eventId),
    enabled: Number.isFinite(eventId),
  });

  const resultsQuery = useQuery({
    queryKey: ["eventResults", eventId],
    queryFn: () => api.eventResults(eventId),
    enabled: Number.isFinite(eventId),
  });

  const matchesQuery = useQuery({
    queryKey: ["eventMatches", eventId],
    queryFn: () => api.matches({ eventId, pageSize: 200 }),
    enabled: Number.isFinite(eventId),
  });

  // Reverse lookup: playerId -> { resultId, hasDecklist } for the Partidas
  // table, so each deck cell can link to the correct decklist page (which
  // is keyed by event_result id, not player id). Must be declared above any
  // early return — React's Rules of Hooks require a stable hook order.
  const decklistByPlayer = useMemo(() => {
    const map = new Map<number, { resultId: number; hasDecklist: boolean }>();
    for (const r of resultsQuery.data ?? []) {
      map.set(r.playerId, { resultId: r.id, hasDecklist: r.hasDecklist });
    }
    return map;
  }, [resultsQuery.data]);

  const eventName = eventQuery.data?.name;
  usePageTitle(eventName ? `Evento — ${eventName}` : null);

  if (eventQuery.isLoading || resultsQuery.isLoading) return <Loading />;
  if (eventQuery.error) return <ErrorBox error={eventQuery.error} />;
  if (!eventQuery.data) return <ErrorBox error={new Error("Event not found")} />;

  const event = eventQuery.data;
  const results = resultsQuery.data ?? [];
  const matches = matchesQuery.data?.matches ?? [];

  const deckHrefFor = (playerId: number | null): string | null => {
    if (playerId === null) return null;
    const entry = decklistByPlayer.get(playerId);
    if (!entry || !entry.hasDecklist) return null;
    return `/events/${eventId}/decks/${entry.resultId}`;
  };

  const subarchetypeHrefFor = (
    subarchetypeId: number | null,
  ): string | null => {
    if (subarchetypeId === null) return null;
    return `/subarchetypes/${subarchetypeId}`;
  };

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/"
          className="text-sm font-medium text-brand-700 hover:underline"
        >
          ← Voltar para ligas
        </Link>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-2 text-ink">
          {event.name}
        </h1>
        <p className="text-sm text-[#8a8170] mt-1">
          {event.date}
          {event.format ? ` · ${event.format}` : ""}
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="font-display font-bold text-lg text-ink">
          Resultados do Evento
        </h2>
        <div className="rounded-2xl border border-line bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-4 py-3 w-16 font-bold">#</th>
                <th className="text-left px-4 py-3 font-bold">Player</th>
                <th className="text-left px-4 py-3 font-bold">Deck</th>
                <th className="text-left px-4 py-3 font-bold">Subarquétipo</th>
                <th className="text-left px-4 py-3 font-bold">Arquétipo</th>
                <th className="text-right px-4 py-3 w-24 font-bold">Pontos</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.id} className="border-t border-[#f3efe6]">
                  <td className="px-4 py-2.5 tabular-nums text-[#9a9182]">
                    {r.rank}
                  </td>
                  <td className="px-4 py-2.5 font-medium text-ink">
                    {r.playerName}
                  </td>
                  <td className="px-4 py-2.5 text-[#6b6356]">
                    {r.deckName ? (
                      r.hasDecklist ? (
                        <Link
                          to={`/events/${eventId}/decks/${r.id}`}
                          className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
                        >
                          {r.deckName}
                        </Link>
                      ) : (
                        r.deckName
                      )
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-[#6b6356]">
                    {r.subarchetypeName ? (
                      r.subarchetypeId !== null ? (
                        <Link
                          to={`/subarchetypes/${r.subarchetypeId}`}
                          className="text-brand-700 hover:underline decoration-dotted underline-offset-2"
                        >
                          {r.subarchetypeName}
                        </Link>
                      ) : (
                        r.subarchetypeName
                      )
                    ) : (
                      <span className="italic text-[#b8b0a0]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.archetypeName ? (
                      <span className="inline-block rounded-full bg-brand-50 text-brand-900 text-xs font-semibold px-2 py-0.5">
                        {r.archetypeName}
                      </span>
                    ) : (
                      <span className="italic text-[#b8b0a0]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-bold font-display text-ink">
                    {r.points}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 py-8">
                    Sem resultados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="font-display font-bold text-lg text-ink">Partidas</h2>
        <div className="rounded-2xl border border-line bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-50 text-brand-900">
              <tr className="text-[11px] uppercase tracking-wide">
                <th className="text-left px-4 py-3 w-16 font-bold">Rd</th>
                <th className="text-left px-4 py-3 font-bold">Player 1</th>
                <th className="text-left px-4 py-3 font-bold">Deck 1</th>
                <th className="text-center px-4 py-3 w-20 font-bold">Placar</th>
                <th className="text-left px-4 py-3 font-bold">Deck 2</th>
                <th className="text-left px-4 py-3 font-bold">Player 2</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={m.id} className="border-t border-[#f3efe6]">
                  <td className="px-4 py-2.5 tabular-nums text-[#9a9182]">
                    {m.round ?? "—"}
                  </td>
                  <td className="px-4 py-2.5 font-medium text-ink">
                    {m.player1Name}
                    {m.winnerId === m.player1Id && <WinnerTag />}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <DeckCell
                      deckName={m.player1DeckName}
                      subarchetype={m.player1SubarchetypeName}
                      subarchetypeHref={subarchetypeHrefFor(
                        m.player1SubarchetypeId,
                      )}
                      archetype={m.player1ArchetypeName}
                      deckHref={deckHrefFor(m.player1Id)}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center tabular-nums font-display font-bold text-ink align-top">
                    {m.player1Wins}–{m.player2Wins}
                    {m.draws > 0 ? `–${m.draws}` : ""}
                  </td>
                  <td className="px-4 py-2.5 align-top">
                    <DeckCell
                      deckName={m.player2DeckName}
                      subarchetype={m.player2SubarchetypeName}
                      subarchetypeHref={subarchetypeHrefFor(
                        m.player2SubarchetypeId,
                      )}
                      archetype={m.player2ArchetypeName}
                      deckHref={deckHrefFor(m.player2Id)}
                    />
                  </td>
                  <td className="px-4 py-2.5 font-medium text-ink">
                    {m.player2Name ?? (
                      <span className="italic text-slate-400">bye</span>
                    )}
                    {m.winnerId !== null && m.winnerId === m.player2Id && (
                      <WinnerTag />
                    )}
                  </td>
                </tr>
              ))}
              {matches.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-500 py-8">
                    Sem partidas registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
